import React, { FC, useState, useContext } from 'react';

import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';

import { AuthenticationContext } from '../../../homeModules/auth/contexts/AuthenticationContext';
import { ChillerLogsContext } from '../contexts/ChillerLogsContext';

import axios from '../../../sharedModules/utils/axios.utils';

const SubmissionButtons: FC = () => {
  const { isAdminUser, isApprover } = useContext(AuthenticationContext);
  const { chillerLog, setChillerLog, setSubmitDialogOpen } = useContext(ChillerLogsContext);

  let [submitting, setSubmitting] = useState(false);
  let [submissionError, setSubmissionError] = useState('');

  // Ensure permissions are properly evaluated even after loading from localStorage
  // Convert to boolean to handle undefined case when user context is loading
  let userCanApprove = Boolean(isAdminUser) || Boolean(isApprover);

  const submitChillerLog = async () => {
    setSubmitting(true);
    try {
      // Re-check permissions at submit time to ensure latest user context is used
      const canApprove = Boolean(isAdminUser) || Boolean(isApprover);
      const submitStatus = canApprove ? 'approved' : 'pending_approval';

      const response: any = await axios.put(`/chiller_logs/chiller_logs/${chillerLog.id}`, {
        ...chillerLog,
        status: submitStatus,
      });

      setChillerLog(response?.data?.result?.spreadsheet_details);
      setSubmitDialogOpen(true);
    } catch (error) {
      console.error('Error submitting chiller log:', error);
      setSubmissionError('Error submitting chiller log.');
    } finally {
      setSubmitting(false);
    }
  };

  const statusLabels = {
    not_started: { submit: 'Submit', approve: 'Submit and Approve' },
    in_progress: { submit: 'Submit', approve: 'Submit and Approve' },
    overdue: { submit: 'Submit', approve: 'Submit and Approve' },
    closed_no_log: { submit: 'Closed', approve: 'Closed' },
    pending_approval: { submit: 'Pending Approval', approve: 'Approve' },
    manual_review: { submit: 'Manual Review', approve: 'Manual Review' },
    complete: { submit: 'Complete', approve: 'Complete' },
    not_needed: { submit: 'No Log Needed', approve: 'No Log Needed' },
    approved: { submit: 'Approved', approve: 'Approved' },
  };

  const disabledStatuses = ['closed_no_log', 'manual_review', 'complete', 'not_needed', 'approved'];
  const chillerStatus = chillerLog?.status as keyof typeof statusLabels;
  const isDisabledStatus = disabledStatuses.includes(chillerStatus);

  const buttonLabel = userCanApprove
    ? statusLabels[chillerStatus]?.approve || 'Approve'
    : statusLabels[chillerStatus]?.submit || 'Submit';

  let disableButton = submitting || isDisabledStatus || (!userCanApprove && chillerStatus === 'pending_approval');

  return (
    <Grid container style={{ marginTop: 20, marginBottom: 20 }}>
      <Grid item xs={3}>
        {/* minWidth ensures the button width remains when the status label changes */}
        <Button onClick={submitChillerLog} variant="contained" disabled={disableButton} style={{ minWidth: '190px' }}>
          {buttonLabel}
          {submitting && <CircularProgress size={24} style={{ marginLeft: 10 }} />}
        </Button>
      </Grid>
      <Grid item xs={12} className="no-print">
        {submissionError.length > 0 && <p style={{ color: 'red' }}>{submissionError}</p>}
      </Grid>
    </Grid>
  );
};

export default SubmissionButtons;
